import { apiSlice } from './api-slice';
import { Response, Sortable, Role, PaginatedRole, Action } from '../models';
import { PAGINATION_SIZE as paginatorSize } from '../constants';
import { RoleForm } from '../modules/roles/models';

export const rolesApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAllActions: builder.query<
      Response<{ id: number; permissionName: string; actions: Action[] }[]>,
      void
    >({
      query: () => ({
        url: `role/actions`,
        method: 'GET',
      }),
    }),
    getAllRoles: builder.query<Response<Sortable<Role>>, PaginatedRole>({
      query: ({ page, name, size, basicInfoId, reportToID, status }) => ({
        url: `role`,
        method: 'GET',
        params: {
          basicInfoId,
          size: size || paginatorSize,
          page,
          name,
          status,
          reportToID,
        },
      }),
      providesTags: result =>
        result?.data.content.length
          ? result.data.content.map(({ id }) => ({ type: 'Role', id }))
          : ['Role'],
    }),
    getRoleById: builder.query<Response<Role>, number>({
      query: roleId => ({
        url: `role/fetchById`,
        method: 'GET',
        params: {
          roleId,
        },
      }),
      providesTags: (_, __, id) => [{ type: 'Role', id }],
    }),
    createRole: builder.mutation<
      Response<Role>,
      Omit<RoleForm, 'reportTo'> & { reportTo: { id: number } | null }
    >({
      query: data => ({
        url: 'role',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Role' }],
    }),
    updateRole: builder.mutation<
      Response<Role>,
      Omit<Role, 'companyBasicInfo' | 'reportTo'> & {
        reportTo: { id: number } | null;
      }
    >({
      query: data => ({
        url: 'role',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: result => {
        return [{ type: 'Role', id: result?.data.id }];
      },
    }),
    deleteRole: builder.mutation<Response<Role>, { id: number }>({
      query: data => ({
        url: 'role',
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: () => {
        return [{ type: 'Role' }];
      },
    }),
  }),
});

export const {
  useGetAllActionsQuery,
  useGetAllRolesQuery,
  useGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = rolesApiSlice;
